import { useEffect, useRef } from "react";
import { RadioTower, Check, BookOpen, ChevronRight, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { BulletinMessage } from "../BulletinMessage";
import { useUnreadBulletins } from "../../hooks/useUnreadBulletins";
import { signApiUrl } from "../../lib/signApiUrl";
import BACKEND_URL from "../../lib/apiConfig";
import "./bulletin-popup.css";

export function BulletinPopup({ userId, onNavigateToArticle }) {
  const { current, count, acknowledge, acknowledging, error } = useUnreadBulletins(userId);
  const bodyRef = useRef(null);
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = 0; }, [current?.id]);
  if (!current) return null;
  return <Dialog open onOpenChange={() => {}}>
    <DialogContent className="bulletin-dialog bg-[#1A1A1E] border-white/10"
      overlayClassName="bulletin-overlay" showCloseButton={false}
      onEscapeKeyDown={event => event.preventDefault()} onInteractOutside={event => event.preventDefault()}
      onOpenAutoFocus={event => { event.preventDefault(); bodyRef.current?.focus(); }}
      data-testid="bulletin-popup">
      <DialogDescription className="sr-only">Team bulletin. Acknowledge receipt after reading.</DialogDescription>
      <header className="bulletin-popup-header" data-testid="bulletin-popup-header">
        <div className="w-10 h-10 rounded-xl bg-[#007AFF]/20 flex items-center justify-center shrink-0"><RadioTower className="h-5 w-5 text-[#007AFF]" /></div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] text-[#007AFF] font-semibold uppercase">Bulletin</p>
          <DialogTitle className="text-lg font-bold text-white leading-tight" data-testid="bulletin-popup-title">{current.title}</DialogTitle>
        </div>
        {count > 1 && <span className="text-xs text-zinc-400 shrink-0" data-testid="bulletin-popup-count">1 / {count}</span>}
      </header>
      <div ref={bodyRef} className="bulletin-popup-body" tabIndex={0} role="region" aria-label="Bulletin content"
        data-testid="bulletin-popup-scroll-area">
        <BulletinMessage id="bulletin-popup-description" text={current.message} className="text-sm text-zinc-300 leading-relaxed" data-testid="bulletin-popup-message" />
        {!!current.images?.length && <div className="bulletin-popup-gallery" data-testid="bulletin-popup-images">
          {current.images.map((img, i) => <div className="bulletin-popup-media-item" key={i}>
            <img src={img} alt={`Bulletin attachment ${i + 1}`} data-testid={`bulletin-popup-image-${i}`} />
          </div>)}
        </div>}
        {!!current.media?.length && <div className="bulletin-popup-gallery" data-testid="bulletin-popup-media">
          {current.media.map((media, i) => <div key={media.storage_path || i} className="bulletin-popup-media-item">
            {media.kind === "video"
              ? <video src={signApiUrl(`${BACKEND_URL}/api/team-reviews/media/${media.storage_path}`)} controls preload="metadata"
                  aria-label={`Bulletin video ${i + 1}`} data-testid={`bulletin-popup-video-${i}`} />
              : <img src={signApiUrl(`${BACKEND_URL}/api/team-reviews/media/${media.storage_path}`)}
                  alt={`Bulletin media attachment ${i + 1}`} data-testid={`bulletin-popup-media-image-${i}`} />}
          </div>)}
        </div>}
        {current.knowledge_article_id && onNavigateToArticle && <button onClick={() => onNavigateToArticle(current.knowledge_article_id)}
          className="mt-3 flex items-center gap-2 p-3 rounded-lg bg-[#007AFF]/10 border border-[#007AFF]/30 text-[#007AFF] text-sm font-medium w-full"
          data-testid="bulletin-kb-link"><BookOpen className="h-4 w-4 shrink-0" /><span className="flex-1 text-left">View Knowledge Article</span><ChevronRight className="h-4 w-4 shrink-0" /></button>}
      </div>
      <footer className="bulletin-popup-footer border-t border-white/10" data-testid="bulletin-popup-footer">
        {error && <p role="alert" className="text-sm text-red-500 mb-2" data-testid="bulletin-acknowledge-error">{error}</p>}
        <button onClick={acknowledge} disabled={acknowledging}
          className="w-full min-h-12 py-3 rounded-xl bg-[#39FF14] hover:bg-[#32E012] text-black font-bold text-base transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="bulletin-acknowledge-btn">
          {acknowledging ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />}
          {acknowledging ? "Acknowledging…" : "Got it"}
        </button>
        {current.created_at && <p className="text-center text-[10px] text-zinc-500 mt-2" data-testid="bulletin-popup-date">
          {new Date(current.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
        </p>}
      </footer>
    </DialogContent>
  </Dialog>;
}